import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTable, usePagination } from 'react-table';
import axiosApi from '../Utils/axiosApi';
import './UserListPage.css'; // You can rename it later if needed
import HeaderMenu from '../Header/HeaderMenu';
import checkPermission from '../Utils/checkPermission';
import {
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import CreateCompany from '../UserMaster/CreateCompany';

const CompanyListPage = () => {
  const [data, setData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  useEffect(() => {
    fetchCompanyList();
  }, []);

  const fetchCompanyList = async () => {
    try {
      const response = await axiosApi.post('/company/getCompany', {});
      setData(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleViewDetails = useCallback((company) => {
    setSelectedCompany(company);
    setDialogOpen(true);
  }, []);

  const handleDeleteClick = (companyId) => {
    setCompanyToDelete(companyId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!companyToDelete) return;

    axiosApi
      .post('/company/deleteCompany', { companyIds: [companyToDelete] })
      .then(() => {
        setDeleteMessage('Company has been deleted.');
        fetchCompanyList();
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error('Error deleting company:', error);
        setDeleteMessage('Failed to delete the company. Please try again.');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setConfirmDeleteOpen(false);
        setCompanyToDelete(null);
      });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCompany(null);
  };

  const columns = useMemo(
    () => [
      { Header: 'Company Name', accessor: 'companyName' },
      { Header: 'Company Type', accessor: 'companyType' },
      { Header: 'Description', accessor: 'description' },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div className="actions-container">
            <Button disabled={!checkPermission("Admin", "Companys", "VIEW")}
              size="small"
              variant="outlined"
              color="primary"
			  
              onClick={() => handleViewDetails(row.original)}
              
            >
              View
            </Button>
            <Button disabled={!checkPermission("Admin", "Companys", "DELETE")}
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleDeleteClick(row.original.id)}
             
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDetails]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex, pageSize },
    setPageSize,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    usePagination
  );

  return (
    <>
      <HeaderMenu />
      <div className="user-list">
        <h2>Company List</h2>
<div className="table-container">
        <table {...getTableProps()} className="table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.original.id || row.index}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} key={cell.column.id} data-label={cell.column.Header}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
</div>
        {/* MUI Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(e, value) => gotoPage(value - 1)}
            color="primary"
            shape="rounded"
          />

          <div>
            <label htmlFor="rows-per-page" style={{ marginRight: '8px' }}>Rows per page:</label>
            <select
              id="rows-per-page"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 15, 20].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={deleteMessage}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />

        {/* Create/Edit Dialog */}
        <Dialog
  open={dialogOpen}
  onClose={(event, reason) => {
    // Only close on Escape key, not on backdrop click
    if (reason !== "backdropClick") {
      setDialogOpen(false);
    }
  }}
  fullWidth
  maxWidth="sm"
>
          <DialogTitle>{selectedCompany ? 'Edit Company' : 'Create Company'}</DialogTitle>
          <DialogContent>
            <CreateCompany
              open={dialogOpen}
              onClose={handleCloseDialog}
              company={selectedCompany}
              isDialog={true}
			  onSuccess={() => {
        fetchCompanyList();   
        
      }}
            />
          </DialogContent>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <p>Are you sure you want to delete this company?</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default CompanyListPage;
